import {
  AgentProfile,
  AppicationErrorReason,
  ApplicationError,
  Invitee
} from "@closerplatform/spinner-openapi";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { logger } from "@fidget/common/utils";
import { notEmpty } from "@fidget/common/utils/not-empty";
import { FieldState, FormState } from "formstate";
import { flow, isEmpty, uniq } from "lodash";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useStore } from "../../../models";
import { useAgentServices } from "../../../models/agentServices";
import { usePreloader } from "../../HOC/withPreloading/withPreloading";
import {
  ListHookEditable,
  ListHookWithFilters,
  ListHookWithSelectables,
  Params
} from "../models/useSettingsList/types";
import {
  useSettingsListEditable,
  withFilters,
  withSelectables
} from "../models/useSettingsList/useSettingsList";
import { ExistingAdviserForm, InviteAdvisersForm, NewAdviser } from "./types";
import {
  copyExistingAdviserForm,
  createInviteForm,
  mapAdviserToForm,
  mapExistingFormToBatchPatchForms,
  mapInviteFormToApiForm,
  mapInviteFormToInvitees
} from "./utils/utils";
import { extractApplicationError } from "@fidget/common/utils/errorHelpers";

const UsersSettingsContext = createContext<UsersSettingsProviderValue | undefined>(undefined);
UsersSettingsContext.displayName = "UsersSettingsContext"; // for name to display in dev tools

type HookParams = Params<ExistingAdviserForm, InviteAdvisersForm, AgentProfile>;
type HookEditable = ListHookEditable<ExistingAdviserForm, InviteAdvisersForm>;
type HookSelectable = ListHookWithSelectables<
  ExistingAdviserForm,
  InviteAdvisersForm,
  HookEditable
>;
type HookFilterable = ListHookWithFilters<ExistingAdviserForm, InviteAdvisersForm, HookSelectable>;

export type UsersSettingsProviderValue = HookFilterable & {
  invitees: Invitee[];
  removeInvitee(adviser: NewAdviser): void;
  revokeInvitee(invitee: Invitee): Promise<void>;
  removeInviteeFromUi(invitee: Invitee): void;
  addAdvisersToGroup(groupId: string, adviserIds: string[]): void;
  removeAdvisersFromGroup(groupId: string, adviserIds: string[]): void;
};

const useListHook = flow<[HookParams], HookEditable, HookSelectable, HookFilterable>(
  useSettingsListEditable,
  withSelectables,
  withFilters
);

const useUsersSettingsState = () => {
  const { spinnerService } = useAgentServices();
  const { setLoadingState } = usePreloader();
  const { uiStore, agentStore, agentGroupStore } = useStore();
  const [agentGroupsAdvisers, setAgentGroupsAdvisers] = useState<{
    [agentGroupId: string]: FieldState<string[]>;
  }>({});
  const [invitees, setInvitees] = useState<Invitee[]>([]);

  useEffect(() => {
    agentGroupStore.load();

    if (uiStore.canDisplayAdvisersConfiguration()) {
      spinnerService
        .getPendingAgents()
        .then(setInvitees)
        .catch((e) => {
          logger.error({ message: "Couldn't load org invitees", error: e });
          uiStore.addGenericErrorToast();
        });
    }
  }, []);

  const addAdvisersToGroup = (groupId: string, adviserIds: string[]) => {
    const groupAdvisers = agentGroupsAdvisers[groupId];
    if (groupAdvisers) {
      groupAdvisers.onChange(uniq([...adviserIds, ...groupAdvisers.$]));
    } else {
      const currentGroup = agentGroupStore.getById(groupId);
      if (currentGroup) {
        const targetAdvisers = uniq([...adviserIds, ...currentGroup.adviserIds]);
        setAgentGroupsAdvisers((c) => ({ ...c, [groupId]: new FieldState(targetAdvisers) }));
      } else {
        logger.error("Tried to update non-existing group", groupId);
      }
    }
  };

  const removeAdvisersFromGroup = (groupId: string, adviserIds: string[]) => {
    const groupAdvisers = agentGroupsAdvisers[groupId];
    if (groupAdvisers) {
      groupAdvisers.onChange(groupAdvisers.$.filter((id) => !adviserIds.includes(id)));
    } else {
      const currentGroup = agentGroupStore.getById(groupId);
      if (currentGroup) {
        const targetAdvisers = currentGroup.adviserIds.filter((id) => !adviserIds.includes(id));
        setAgentGroupsAdvisers((c) => ({ ...c, [groupId]: new FieldState(targetAdvisers) }));
      } else {
        logger.error("Tried to update non-existing group", groupId);
      }
    }
  };

  const reset = () => {
    listHook.deselectAll();
    setAgentGroupsAdvisers({});
  };

  const inviteAdivsers = (form: InviteAdvisersForm) => {
    return spinnerService.inviteManyAgents(mapInviteFormToApiForm(form));
  };

  const isEmailTaken = (adviser: NewAdviser): boolean => {
    const { email } = adviser.$;

    return (
      listHook.items.some((agent) => agent.$.email.$ === email.$) ||
      invitees.some((invitee) => invitee.email === email.$)
    );
  };

  const validateInviteesForm = async (form: InviteAdvisersForm): Promise<boolean> => {
    const validate = await form.validate();
    if (validate.hasError) {
      uiStore.addErrorToast(intl.formatMessage("settings.advisers.error.emailFormat"));
      return false;
    }

    if (uniq(form.$.map((a) => a.$.email.$)).length !== form.$.length) {
      uiStore.addErrorToast(intl.formatMessage("settings.advisers.error.emailNonUnique"));
      return false;
    }

    if (form.$.some(isEmailTaken)) {
      uiStore.addErrorToast(intl.formatMessage("settings.advisers.error.alreadyInvited"));
      return false;
    }

    return true;
  };

  const submitInvitees = async () => {
    const { newItem, discardNewItem } = listHook;
    if (!newItem || !(await validateInviteesForm(newItem))) {
      return;
    }

    try {
      await inviteAdivsers(newItem);

      setInvitees((current) => [...current, ...mapInviteFormToInvitees(newItem)]);
      discardNewItem();
    } catch (e) {
      logger.error({ message: "Couldn't invite advisers", error: e });
      const responseBody: ApplicationError = await extractApplicationError(e as Response);

      if (responseBody.error === AppicationErrorReason.AgentEmailAlreadyUsed) {
        uiStore.addErrorToast(intl.formatMessage("error.emailAlreadyUsed"));
      } else {
        uiStore.addGenericErrorToast();
      }
    }
  };

  const removeInvitee = (adviser: NewAdviser) => {
    const { newItem, setNewItem, discardNewItem } = listHook;
    if (newItem) {
      const remaining = newItem.$.filter((a) => a !== adviser);
      if (remaining.length === 0) {
        discardNewItem();
      } else {
        setNewItem(new FormState(remaining));
      }
    }
  };

  const revokeInvitee = (invitee: Invitee) => {
    return spinnerService.revokeInvitee(invitee.email);
  };

  const removeInviteeFromUi = (invitee: Invitee) => {
    setInvitees((current) => current.filter((i) => i.email !== invitee.email));
  };

  const discardPristineNew = () => {
    const { newItem, discardNewItem } = listHook;
    if (!newItem) {
      return;
    }

    if (newItem.$.some((form) => !!form.$.email.$)) {
      return;
    }

    discardNewItem();
  };

  const listHook = useListHook({
    apiGetItems: () =>
      agentStore.loadAgentProfiles().then((profiles) => profiles.filter((a) => !a.isBot)),
    batchUpdateItems: async (items) => {
      const forms = items.map(mapExistingFormToBatchPatchForms);
      const formsWithPermissions = forms.map((form) => {
        return {
          id: form.id,
          tagGroupIds: uiStore.canDisplayAdvisersAssignments() ? form.tagGroupIds : undefined,
          autoAssignedLimit: uiStore.canDisplayAdvisersAutoAssignedLimit()
            ? form.autoAssignedLimit
            : undefined,
          tagIds: uiStore.canDisplayAdvisersAssignments() ? form.tagIds : undefined,
          roleId: uiStore.canDisplayAdvisersConfiguration() ? form.roleId : undefined,
          validFrom: uiStore.canDisplayAdvisersConfiguration() ? form.validFrom : undefined,
          validTo: uiStore.canDisplayAdvisersConfiguration() ? form.validTo : undefined
        };
      });

      const getPatchAdvisersUpdates = () => {
        spinnerService.patchAdvisers(formsWithPermissions);
      };

      const getAgentGroupsUpdates = () => {
        return uiStore.canDisplayAgentGroupsConfiguration() && !isEmpty(agentGroupsAdvisers)
          ? spinnerService
              .patchAgentGroups(
                Object.entries(agentGroupsAdvisers).map(([agentGroupId, advisers]) => {
                  return { id: agentGroupId, advisers: advisers.$ };
                })
              )
              .then((groups) => {
                agentGroupStore.setGroups(groups);
              })
              .then(() => setAgentGroupsAdvisers({}))
          : undefined;
      };

      const updates = [getPatchAdvisersUpdates(), getAgentGroupsUpdates()].filter(notEmpty);

      await Promise.all(updates);
    },
    apiDeleteItem: (id) => spinnerService.removeAgent(id),
    mapItemToForm: mapAdviserToForm,
    createNewForm: createInviteForm,
    copyExistingForm: copyExistingAdviserForm,

    saveErrorId: "settings.error.cantSave",
    onSubmitCallback: reset,
    onDiscardCallback: reset,
    overridenSaveNewItem: submitInvitees,
    onInitializeResult: setLoadingState
  });

  useEffect(() => {
    listHook.items.forEach((item) => {
      const setEdited = () => listHook.setItemEdited(item);
      item.$.tagIds.onUpdate(setEdited);
      item.$.roleId.onUpdate(setEdited);
      item.$.validTo.onUpdate(setEdited);
      item.$.validFrom.onUpdate(setEdited);
      item.$.autoAssignedLimit.onUpdate(setEdited);
      item.$.tagGroupIds.onUpdate(setEdited);
      item.$.agentGroupIds.onUpdate(setEdited);
    });
  }, [listHook.items]);

  return {
    ...listHook,
    addAdvisersToGroup,
    removeAdvisersFromGroup,
    invitees,
    removeInvitee,
    revokeInvitee,
    removeInviteeFromUi,
    discardPristineNew
  };
};

export const UsersSettingsProvider = ({ children }: React.PropsWithChildren<{}>) => {
  const value = useUsersSettingsState();

  return <UsersSettingsContext.Provider value={value}>{children}</UsersSettingsContext.Provider>;
};

export const useUsersSettings = (): UsersSettingsProviderValue => {
  const context = useContext(UsersSettingsContext);

  if (context === undefined) {
    throw new Error("useUsersSettings must be used within a UsersSettingsProvider");
  }

  return context as UsersSettingsProviderValue;
};
